import { Dayjs } from 'dayjs'

import { OptimusSchemas } from '~/clients/optimus-client'
import { useStore } from '~/store/store'

import { StaffingPlanExecution, useStaffingPlanExecutions } from '../'

export type StaffingPlanExecutionStatus = OptimusSchemas['StaffingPlanExecutionStatus']

const IGNORED_STATUSES: StaffingPlanExecutionStatus[] = ['rejected', 'discarded', 'stopped']

function getMonthYear(selectedDate: Dayjs) {
    return { year: selectedDate.year(), month: selectedDate.month() + 1 }
}

export function useStaffingPlanExecutionsForSelectedMonth(options?: { selectedDate?: Dayjs }) {
    const storeSelectedDate = useStore(state => state.appFilters.selectedDate)
    const selectedDate = options?.selectedDate ?? storeSelectedDate
    const { year, month } = getMonthYear(selectedDate)

    const executions = useStaffingPlanExecutions({
        where: { year, month },
        orderBy: ['created_at'],
    })

    return executions.filter(execution => !IGNORED_STATUSES.includes(execution.status))
}

export function useCurrentDepartmentStaffingPlanExecution(options?: { departmentKey?: number | 'all'; selectedDate?: Dayjs }): StaffingPlanExecution | null {
    const storeDepartmentKey = useStore(state => state.appFilters.departmentKey)
    const departmentKey = options?.departmentKey ?? storeDepartmentKey
    const executions = useStaffingPlanExecutionsForSelectedMonth(options?.selectedDate ? { selectedDate: options.selectedDate } : undefined)

    if (departmentKey === 'all') {
        return null
    }

    return executions.findLast(execution => execution.department_id === departmentKey) ?? null
}

export function useIsStaffingPlanExecutionBlocking() {
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const executions = useStaffingPlanExecutionsForSelectedMonth()

    if (departmentKey === 'all') {
        return executions.some(({ status }) => status === 'started')
    }

    const currentDepartmentExecution = executions.findLast(execution => execution.department_id === departmentKey)
    return currentDepartmentExecution?.status === 'started'
}
